import { useCallback } from 'react';
import { UseFormSetError, UseFormClearErrors } from 'react-hook-form';
import { numberToCurrencyString } from 'further-ui/utils';
import { TaxReliefElegibleType, ConsiderationType } from 'further-types/exit';
import { ExitDetailsFormValues } from '../index';
import { PreviousExitDetails } from '../types';

/**
 * Validates tax relief eligibility fields (used for both Cash and Share consideration)
 */
const validateTaxRelief = (
  data: ExitDetailsFormValues,
  setError: UseFormSetError<ExitDetailsFormValues>,
): boolean => {
  let isValid = true;

  if (!data.taxReliefElegible) {
    setError('taxReliefElegible', {
      message: 'Please select an option.',
    });
    isValid = false;
  }

  if (
    data.taxReliefElegible === TaxReliefElegibleType.Partial &&
    !data.taxReliefElegibleAllocationIds?.length
  ) {
    setError('taxReliefElegibleAllocationIds', {
      message: 'Please select at least one allocation.',
    });
    isValid = false;
  }

  return isValid;
};

/**
 * Validates cash consideration specific fields
 */
const validateCashConsideration = (
  data: ExitDetailsFormValues,
  setError: UseFormSetError<ExitDetailsFormValues>,
  isEditMode: boolean,
  isBeingCorrected: boolean,
  previousExitDetails: PreviousExitDetails | undefined,
): boolean => {
  let isValid = true;

  if (isEditMode && !isBeingCorrected) {
    const previousSharePrice = previousExitDetails?.sharePrice
      ? Number(previousExitDetails.sharePrice)
      : 0;
    if (!data.sharePrice || Number(data.sharePrice) < previousSharePrice) {
      setError('sharePrice', {
        message: `The received value share price must be equal to or greater than the original received value share price of ${numberToCurrencyString(
          previousSharePrice,
        )}. To edit an exit to a lower overall share price, please delete the original exit and process it again.`,
      });
      isValid = false;
    }
  } else if (!isEditMode || isBeingCorrected) {
    if (data.sharePrice === '' || Number(data.sharePrice) < 0) {
      setError('sharePrice', {
        message: 'Please enter the share price.',
      });
      isValid = false;
    }
  }

  if (typeof data.chargeAccruedFees !== 'boolean') {
    setError('chargeAccruedFees', {
      message: 'Please select an option.',
    });
    isValid = false;
  }

  if (isEditMode) {
    const value = data.taxableValueSharePrice;
    if (value === '' || value == null || isNaN(Number(value))) {
      setError('taxableValueSharePrice', {
        message: 'Please enter a value for the taxable value share price.',
      });
      isValid = false;
    }
  }

  if (Number(data.sharePrice) > Number(data.taxableValueSharePrice)) {
    setError('taxableValueSharePrice', {
      message:
        'The taxable value share price must be equal to or greater than the received value share price.',
    });
    isValid = false;
  }

  return isValid;
};

/**
 * Validates share consideration specific fields
 */
const validateShareConsideration = (
  data: ExitDetailsFormValues,
  setError: UseFormSetError<ExitDetailsFormValues>,
  isBeingCorrected: boolean,
): boolean => {
  let isValid = true;

  if (!data.shareConsiderationCompanyId) {
    setError('shareConsiderationCompanyId', {
      message: 'Please select a share consideration company.',
    });
    isValid = false;
  }

  if (!data.shareConsiderationShareClassId) {
    setError('shareConsiderationShareClassId', {
      message: 'Please select a consideration share class.',
    });
    isValid = false;
  }

  if (
    !data.totalConsiderationShares ||
    Number(data.totalConsiderationShares) <= 0
  ) {
    setError('totalConsiderationShares', {
      message: 'Please enter the total consideration shares.',
    });
    isValid = false;
  }

  if (Number(data.shareConsiderationValuePerShare) < 0) {
    setError('shareConsiderationValuePerShare', {
      message: 'Please enter the share consideration value per share.',
    });
    isValid = false;
  }

  if (Number(data.shareConsiderationValueTotal) < 0) {
    setError('shareConsiderationValueTotal', {
      message: 'Please enter the total share consideration value.',
    });
    isValid = false;
  }

  if (data.shareConsiderationEisEligible === undefined) {
    setError('shareConsiderationEisEligible', {
      message:
        'Please select whether the share consideration is S/EIS eligible.',
    });
    isValid = false;
  }

  if (!isBeingCorrected && !data.shareConsiderationAllocationMethod) {
    setError('shareConsiderationAllocationMethod', {
      message: 'Please select an allocation method.',
    });
    isValid = false;
  }

  return isValid;
};

/**
 * Hook to manage form validation
 */
export const useExitDetailsFormValidation = (
  setError: UseFormSetError<ExitDetailsFormValues>,
  clearErrors: UseFormClearErrors<ExitDetailsFormValues>,
  isEditMode: boolean,
  isBeingCorrected: boolean,
  previousExitDetails: PreviousExitDetails | undefined,
  maxSharesAvailableForSale: number | undefined,
) => {
  const validateForm = useCallback(
    (data: ExitDetailsFormValues) => {
      clearErrors();
      let isValid = true;

      // Common validations
      if (!data.exitDate) {
        setError('exitDate', {
          message: 'Please select the date the exit happened.',
        });
        isValid = false;
      }

      if (!isEditMode) {
        if (!data.selectedCompany) {
          setError('selectedCompany', {
            message: 'Please select a company.',
          });
          isValid = false;
        }
        if (
          !data.selectedShareClasses ||
          data.selectedShareClasses.length === 0
        ) {
          setError('selectedShareClasses', {
            message: 'Please select at least one share class.',
          });
          isValid = false;
        }
        if (!data.sharesToBeSold || Number(data.sharesToBeSold) <= 0) {
          setError('sharesToBeSold', {
            message: 'Please enter the number of shares to be sold.',
          });
          isValid = false;
        }
        if (data.sharesToBeSold > (maxSharesAvailableForSale ?? 0)) {
          setError('sharesToBeSold', {
            message:
              'Please ensure the number of shares to be sold are equal to, or below, the total number of shares available.',
          });
          isValid = false;
        }
      }

      if (!data.considerationType) {
        setError('considerationType', {
          message: 'Please select a consideration type.',
        });
        isValid = false;
      }

      // Consideration type-specific validations
      if (data.considerationType === ConsiderationType.Cash) {
        isValid =
          validateCashConsideration(
            data,
            setError,
            isEditMode,
            isBeingCorrected,
            previousExitDetails,
          ) && isValid;
      }

      if (data.considerationType === ConsiderationType.Share) {
        isValid =
          validateShareConsideration(data, setError, isBeingCorrected) &&
          isValid;
      }

      // Tax relief validation (applies to both consideration types)
      if (data.considerationType) {
        isValid = validateTaxRelief(data, setError) && isValid;
      }

      return isValid;
    },
    [
      setError,
      clearErrors,
      isEditMode,
      isBeingCorrected,
      previousExitDetails,
      maxSharesAvailableForSale,
    ],
  );

  return { validateForm };
};
